import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageContainer, Alert, Button, Select, Input, DataTable, Skeleton, DatePicker, EmptyState, ErrorBoundary, useToast } from '@bbpos/ui';
import type { ColumnDef, DateRange } from '@bbpos/ui';
import { formatCurrency } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { formatDocAmount } from '@/utils/purchase-currency';
import { GLASS, GLASS_FILTER } from '@/constants/design-tokens';
import { useVendorBills, useVendorBillStats, VendorBillStatusBadge } from '@/features/vendor-bills';
import type { VendorBillListItem, VendorBillStatus } from '@/features/vendor-bills';
import { PoKpi } from '@/features/purchase-orders';
import { useAuthStore } from '@/stores/use-auth-store';
import { storeTimezone, todayIn } from '@/lib/store-clock';
import { ROLE_LEVELS } from '@/types/user';
import { toLocalIsoDate } from '../purchase-orders/date-utils';
import { ClipboardIcon, ClockIcon, CheckCircleIcon, PlusIcon, EyeIcon, BoltIcon } from '../purchase-orders/icons';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'OPEN', label: 'Open' },
  { value: 'PARTIALLY_PAID', label: 'Partially paid' },
  { value: 'PAID', label: 'Paid' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

function fmtDate(value: string | null) {
  return value ? new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
}

function Header({ isManager, onNew }: { isManager: boolean; onNew: () => void }) {
  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-warning-500 via-orange-500 to-danger-500 p-6 text-white shadow-[0_24px_60px_-24px_rgba(234,88,12,0.55)] mb-6 animate-fade-in">
      <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
      <div className="relative flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-white">Vendor Bills</h1>
          <p className="text-orange-100 mt-1">What you owe suppliers for goods ordered or delivered, and what has been paid</p>
        </div>
        {isManager && (
          <Button variant="secondary" onClick={onNew} className="bg-white/95 text-orange-700 hover:bg-white">
            <PlusIcon className="h-4 w-4 mr-1.5" />
            New Vendor Bill
          </Button>
        )}
      </div>
    </div>
  );
}

function VendorBillListContent() {
  const currency = useTenantCurrency();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const profile = useAuthStore((s) => s.profile);
  const isManager = (ROLE_LEVELS[profile?.role ?? ''] ?? 0) >= ROLE_LEVELS.MANAGER;

  const page = Number(searchParams.get('page') ?? '1');
  const status = searchParams.get('status') ?? '';
  const search = searchParams.get('search') ?? '';
  const overdue = searchParams.get('overdue') === '1';
  const from = searchParams.get('from') ?? '';
  const to = searchParams.get('to') ?? '';
  const dateRange: DateRange | undefined = from && to ? { from: new Date(from), to: new Date(to) } : undefined;

  const [searchInput, setSearchInput] = useState(search);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const searchRef = useRef<HTMLInputElement>(null);

  function setParam(key: string, value: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value); else next.delete(key);
      next.set('page', '1');
      return next;
    });
  }
  const handleSearch = useCallback((value: string) => {
    setSearchInput(value);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set('search', value); else next.delete('search');
        next.set('page', '1');
        return next;
      });
    }, 300);
  }, [setSearchParams]);

  const { data, isLoading, isError, error, refetch } = useVendorBills({
    page, limit: 20, status: (status as VendorBillStatus) || undefined, search: search || undefined,
    start_date: from || undefined, end_date: to || undefined, overdue: overdue || undefined,
  });
  const { data: stats } = useVendorBillStats();

  useEffect(() => { if (error) toast({ title: (error as Error).message, variant: 'error' }); }, [error]);
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === '/') { e.preventDefault(); searchRef.current?.focus(); }
      if (e.key === 'n' && isManager) { e.preventDefault(); navigate('/vendor-bills/new'); }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navigate, isManager]);

  const bills = data?.data ?? [];
  const total = data?.meta?.total ?? 0;
  // The business's day, as the Overdue KPI's CURRENT_DATE (QA PU3-003).
  const today = todayIn(storeTimezone(profile));

  const columns: ColumnDef<VendorBillListItem>[] = [
    { key: 'bill_number', header: 'Bill #', render: (_v, r) => (
      <button type="button" onClick={() => navigate(`/vendor-bills/${r.id}`)} className="text-sm text-orange-600 dark:text-orange-400 hover:underline font-medium tabular-nums">{r.bill_number}</button>
    ) },
    { key: 'vendor_invoice_number', header: 'Vendor inv #', render: (_v, r) => <span className="text-sm tabular-nums text-neutral-700 dark:text-neutral-300">{r.vendor_invoice_number ?? '—'}</span> },
    { key: 'supplier_name', header: 'Supplier', render: (_v, r) => <span className="text-sm text-neutral-700 dark:text-neutral-300">{r.supplier_name}</span> },
    { key: 'po_number', header: 'PO', render: (_v, r) => (
      <button type="button" onClick={() => navigate(`/inventory/purchase-orders/${r.purchase_order_id}`)} className="text-sm text-teal-600 dark:text-teal-400 hover:underline tabular-nums">{r.po_number}</button>
    ) },
    { key: 'basis', header: 'Basis', render: (_v, r) => <span className="text-xs uppercase tracking-wide text-neutral-500">{r.basis === 'ORDERED' ? 'Full order' : 'Delivered'}</span> },
    { key: 'bill_date', header: 'Bill date', render: (_v, r) => <span className="text-sm tabular-nums text-neutral-600 dark:text-neutral-400">{fmtDate(r.bill_date)}</span> },
    { key: 'due_date', header: 'Due', render: (_v, r) => {
      const isOverdue = r.due_date && r.due_date.slice(0, 10) < today && (r.status === 'OPEN' || r.status === 'PARTIALLY_PAID');
      return <span className={`text-sm tabular-nums ${isOverdue ? 'text-danger-600 font-medium' : 'text-neutral-600 dark:text-neutral-400'}`}>{fmtDate(r.due_date)}</span>;
    } },
    { key: 'total_amount', header: 'Total', numeric: true, render: (_v, r) => <span className="text-sm tabular-nums text-neutral-800 dark:text-neutral-200">{formatDocAmount(r.total_amount, r.currency_code ?? currency)}{r.currency_code && r.base_total_amount != null && <span className="block text-xs text-neutral-500">≈ {formatCurrency(r.base_total_amount, currency)}</span>}</span> },
    { key: 'balance_due', header: 'Balance', numeric: true, render: (_v, r) => <span className={`text-sm font-semibold tabular-nums ${r.balance_due > 0 ? 'text-neutral-900 dark:text-white' : 'text-neutral-400'}`}>{formatDocAmount(r.balance_due, r.currency_code ?? currency)}</span> },
    { key: 'status', header: 'Status', render: (_v, r) => <VendorBillStatusBadge status={r.status} /> },
    { key: 'actions', header: '', render: (_v, r) => (
      <button type="button" onClick={() => navigate(`/vendor-bills/${r.id}`)} title="View" className="p-1.5 rounded-lg text-neutral-500 hover:text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors"><EyeIcon className="h-4 w-4" /></button>
    ) },
  ];

  if (isLoading && !data) {
    return (<><Skeleton variant="rect" height={96} className="rounded-xl mb-6" /><Skeleton variant="rect" height={400} className="rounded-xl" /></>);
  }
  if (isError) {
    return (
      <>
        <Header isManager={isManager} onNew={() => navigate('/vendor-bills/new')} />
        <Alert variant="danger" title="Couldn't load vendor bills" description={(error as Error)?.message || 'The request failed — this is a server problem, not an empty list.'} action={{ label: 'Retry', onClick: () => void refetch() }} />
      </>
    );
  }
  if (total === 0 && !status && !search && !from && !overdue) {
    return (
      <>
        <Header isManager={isManager} onNew={() => navigate('/vendor-bills/new')} />
        <div className={`${GLASS} p-10`}>
          <EmptyState title="No vendor bills yet" description="Raise a vendor bill from a purchase order when the supplier invoices you — for the delivered goods or the full order." action={isManager ? { label: 'New Vendor Bill', onClick: () => navigate('/vendor-bills/new') } : undefined} />
        </div>
      </>
    );
  }

  return (
    <>
      <Header isManager={isManager} onNew={() => navigate('/vendor-bills/new')} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-6">
        <PoKpi title="Outstanding" value={formatCurrency(stats?.outstanding_total ?? 0, currency)} tone="warning" icon={<ClipboardIcon className="h-5 w-5" />} subtext={`${(stats?.open_count ?? 0) + (stats?.partially_paid_count ?? 0)} open bills`} delay={0} />
        <PoKpi title="Overdue" value={formatCurrency(stats?.overdue_total ?? 0, currency)} tone="danger" icon={<ClockIcon className="h-5 w-5" />} subtext={`${stats?.overdue_count ?? 0} bills past due`} delay={60} />
        <PoKpi title="Paid this month" value={formatCurrency(stats?.paid_this_month ?? 0, currency)} tone="success" icon={<CheckCircleIcon className="h-5 w-5" />} delay={120} />
        <PoKpi title="Settled bills" value={(stats?.paid_count ?? 0).toLocaleString('en-IN')} tone="brand" icon={<BoltIcon className="h-5 w-5" />} delay={180} />
      </div>
      <div className={`${GLASS_FILTER} flex flex-wrap items-end gap-3 mb-4 p-3`}>
        <div className="w-64"><DatePicker variant="range" value={dateRange} onChange={(r) => {
          const range = r as DateRange | undefined;
          setSearchParams((prev) => { const next = new URLSearchParams(prev); if (range?.from && range?.to) { next.set('from', toLocalIsoDate(range.from)); next.set('to', toLocalIsoDate(range.to)); } else { next.delete('from'); next.delete('to'); } next.set('page', '1'); return next; });
        }} placeholder="Bill date range" /></div>
        <div className="w-44"><Select options={STATUS_OPTIONS} value={status} onChange={(v) => setParam('status', typeof v === 'string' ? v : '')} placeholder="All Statuses" /></div>
        <div className="w-60"><Input ref={searchRef} placeholder="Search bill #, invoice #, supplier, PO…" value={searchInput} onChange={handleSearch} /></div>
        <label className="flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-300 pb-2">
          <input type="checkbox" checked={overdue} onChange={(e) => setParam('overdue', e.target.checked ? '1' : '')} className="h-4 w-4 rounded border-neutral-300 text-orange-600 focus:ring-orange-500" />
          Overdue only
        </label>
      </div>
      <div className={`${GLASS} animate-slide-up`}>
        <DataTable<VendorBillListItem> columns={columns} data={bills} loading={isLoading} totalCount={total} currentPage={page} pageSize={20}
          onPageChange={(p) => setSearchParams((prev) => { const next = new URLSearchParams(prev); next.set('page', String(p)); return next; })}
          emptyState={{ title: 'No vendor bills match filters', description: 'Try adjusting your filters' }} />
      </div>
    </>
  );
}

export function VendorBillListPage() {
  return (
    <PageContainer title="Vendor Bills" breadcrumbs={[{ label: 'Finance' }, { label: 'Vendor Bills' }]}>
      <ErrorBoundary><VendorBillListContent /></ErrorBoundary>
    </PageContainer>
  );
}
